import { defineRule, type Rule } from '../types.js';

/**
 * Правила по аппаратным сигналам.
 *
 * Две темы. Первая — рандомизация: антидетект-браузеры подмешивают шум в
 * canvas, WebGL и аудио, чтобы отпечаток не был постоянным. Побочный эффект в
 * том, что он перестаёт быть постоянным и внутри одной страницы, а этого не
 * бывает ни в одном настоящем браузере.
 *
 * Вторая — соответствие железа заявленной системе. Видеокарта, драйвер и
 * графический бэкенд жёстко связаны с ОС, и подменить UA, не тронув WebGL, —
 * самая частая ошибка.
 */

/** Какие семейства GPU физически возможны на каждой платформе. */
const GPU_BY_PLATFORM: Record<string, string[]> = {
  macos: ['apple', 'amd', 'intel', 'nvidia', 'virtual', 'swiftshader', 'llvmpipe'],
  ios: ['apple'],
  windows: ['nvidia', 'amd', 'intel', 'microsoft-basic', 'virtual', 'swiftshader', 'llvmpipe', 'unknown'],
  linux: ['nvidia', 'amd', 'intel', 'llvmpipe', 'virtual', 'swiftshader', 'mali', 'unknown'],
  android: ['adreno', 'mali', 'powervr', 'swiftshader', 'unknown'],
};

const PLATFORM_LABELS: Record<string, string> = {
  macos: 'macOS',
  ios: 'iOS',
  windows: 'Windows',
  linux: 'Linux',
  android: 'Android',
};

const GPU_LABELS: Record<string, string> = {
  apple: 'Apple (M-серия или A-серия)',
  nvidia: 'NVIDIA',
  amd: 'AMD',
  intel: 'Intel',
  adreno: 'Qualcomm Adreno',
  mali: 'ARM Mali',
  powervr: 'PowerVR',
  swiftshader: 'SwiftShader (программный)',
  llvmpipe: 'llvmpipe (программный)',
  'microsoft-basic': 'Microsoft Basic Render Driver',
  virtual: 'виртуальная видеокарта',
};

export const hardwareRules: Rule[] = [
  defineRule({
    meta: {
      id: 'hw.canvas-noise',
      title: 'Canvas отдаёт разный результат при одинаковой отрисовке',
      severity: 'critical',
      category: 'spoofing',
      flags: ['ANTIDETECT', 'SPOOFING'],
      weight: 40,
      evidenceTier: 'corroborating',
      evidenceGroup: 'fingerprint-perturbation',
      requires: ['canvas'],
    },
    evaluate(signals) {
      const canvas = signals.canvas;
      if (!canvas?.supported || canvas.stable !== false) return null;

      const noisePercent = canvas.noiseRatio !== null ? (canvas.noiseRatio * 100).toFixed(3) : '—';
      return {
        explanation:
          'Одна и та же фигура, отрисованная несколько раз подряд, каждый раз дала разные ' +
          'пиксели. Браузер так не работает: рисование детерминировано. Значит, в изображение ' +
          'намеренно подмешивается шум — это делают антидетект-браузеры и расширения против ' +
          'отслеживания.',
        evidence: [
          {
            label: 'Хеши повторных отрисовок',
            detail: (canvas.repeatHashes ?? []).map((h) => h.slice(0, 12)).join(' → '),
          },
          { label: 'Доля изменившихся байтов', detail: `${noisePercent}%` },
        ],
      };
    },
  }),

  defineRule({
    meta: {
      id: 'hw.audio-noise',
      title: 'Аудио-отпечаток нестабилен',
      severity: 'high',
      category: 'spoofing',
      flags: ['ANTIDETECT', 'SPOOFING'],
      weight: 30,
      evidenceTier: 'corroborating',
      evidenceGroup: 'fingerprint-perturbation',
      requires: ['audio'],
    },
    evaluate(signals) {
      const audio = signals.audio;
      if (!audio?.supported || audio.stable !== false) return null;

      return {
        explanation:
          'Один и тот же звуковой сигнал, обсчитанный несколько раз, дал разные значения. ' +
          'Обработка звука полностью детерминирована, поэтому расхождение означает ' +
          'искусственный шум в WebAudio.',
        evidence: [
          {
            label: 'Хеши повторных расчётов',
            detail: (audio.repeatHashes ?? []).map((h) => h.slice(0, 12)).join(' → '),
          },
        ],
      };
    },
  }),

  defineRule({
    meta: {
      id: 'hw.webgl-noise',
      title: 'WebGL отдаёт разный результат при одинаковой отрисовке',
      severity: 'high',
      category: 'spoofing',
      flags: ['ANTIDETECT', 'SPOOFING'],
      weight: 30,
      evidenceTier: 'corroborating',
      evidenceGroup: 'fingerprint-perturbation',
      requires: ['webgl'],
    },
    evaluate(signals) {
      const webgl = signals.webgl;
      const unstable = [webgl?.v1, webgl?.v2].filter((c) => c && c.stable === false);
      if (unstable.length === 0) return null;

      return {
        explanation:
          'Повторная отрисовка одной и той же сцены в WebGL дала другой результат. ' +
          'Видеокарта детерминирована — расхождение указывает на подмену данных, ' +
          'возвращаемых из графического контекста.',
        evidence: unstable.map((c) => ({
          label: `WebGL ${c!.version}`,
          detail: (c!.repeatImageHashes ?? []).map((h) => h.slice(0, 12)).join(' → '),
        })),
      };
    },
  }),

  defineRule({
    meta: {
      id: 'hw.rects-noise',
      title: 'Геометрия элементов нестабильна',
      severity: 'medium',
      category: 'spoofing',
      flags: ['ANTIDETECT', 'PRIVACY_TOOLING'],
      weight: 18,
      evidenceTier: 'corroborating',
      evidenceGroup: 'fingerprint-perturbation',
      requires: ['rects'],
    },
    evaluate(signals) {
      if (signals.rects?.stable !== false) return null;
      return {
        explanation:
          'Размеры одних и тех же элементов страницы меняются между измерениями. ' +
          'Так ведут себя защиты, добавляющие шум в getBoundingClientRect.',
        evidence: [
          {
            label: 'Хеши измерений',
            detail: (signals.rects.repeatHashes ?? []).map((h) => h.slice(0, 12)).join(' → '),
          },
        ],
      };
    },
  }),

  defineRule({
    meta: {
      id: 'hw.gpu-os-mismatch',
      title: 'Видеокарта не встречается на заявленной системе',
      severity: 'critical',
      category: 'spoofing',
      flags: ['SPOOFING', 'ANTIDETECT'],
      weight: 45,
      evidenceTier: 'corroborating',
      evidenceGroup: 'gpu-platform',
      requires: ['webgl', 'navigator'],
    },
    evaluate(signals) {
      const gpuFamily = signals.webgl?.gpuFamily;
      const parsed = signals.navigator?.parsed;
      if (!gpuFamily || gpuFamily === 'unknown' || !parsed) return null;

      const allowed = GPU_BY_PLATFORM[parsed.platformFamily];
      if (!allowed || allowed.includes(gpuFamily)) return null;

      const renderer =
        signals.webgl?.v2?.unmaskedRenderer ?? signals.webgl?.v1?.unmaskedRenderer ?? '—';

      return {
        explanation:
          `Браузер сообщает, что работает на ${PLATFORM_LABELS[parsed.platformFamily] ?? parsed.os}, ` +
          `но видеокарта определяется как ${GPU_LABELS[gpuFamily] ?? gpuFamily}. ` +
          'Такое сочетание физически не существует — либо подменён User-Agent, ' +
          'либо подменены данные графического контекста.',
        evidence: [
          { label: 'Заявленная система', actual: parsed.os + (parsed.osVersion ? ` ${parsed.osVersion}` : '') },
          { label: 'Реальная видеокарта', actual: renderer },
          {
            label: 'Ожидаемые семейства GPU',
            expected: allowed.map((g) => GPU_LABELS[g] ?? g).join(', '),
          },
        ],
      };
    },
  }),

  defineRule({
    meta: {
      id: 'hw.angle-missing-windows',
      title: 'Chrome под Windows без ANGLE',
      severity: 'high',
      category: 'spoofing',
      flags: ['SPOOFING'],
      weight: 30,
      evidenceTier: 'corroborating',
      evidenceGroup: 'gpu-platform',
      requires: ['webgl', 'navigator'],
    },
    evaluate(signals) {
      const parsed = signals.navigator?.parsed;
      const webgl = signals.webgl;
      if (!parsed || !webgl || webgl.usesAngle === null) return null;
      if (parsed.engine !== 'Blink' || parsed.platformFamily !== 'windows') return null;
      if (webgl.usesAngle) return null;

      const renderer = webgl.v2?.unmaskedRenderer ?? webgl.v1?.unmaskedRenderer ?? '—';
      return {
        confidence: 0.8,
        explanation:
          'Chromium под Windows всегда обращается к видеокарте через прослойку ANGLE, ' +
          'и строка рендерера обязана начинаться с «ANGLE (...)». Здесь её нет — ' +
          'значит, либо система на самом деле не Windows, либо строка рендерера подменена.',
        evidence: [
          { label: 'Заявленная система', actual: 'Windows' },
          { label: 'Строка рендерера', expected: 'ANGLE (...)', actual: renderer },
        ],
      };
    },
  }),

  defineRule({
    meta: {
      id: 'hw.software-renderer',
      title: 'Программный рендеринг вместо видеокарты',
      severity: 'medium',
      category: 'hardware',
      flags: ['VIRTUAL_MACHINE', 'AUTOMATION'],
      weight: 20,
      evidenceTier: 'corroborating',
      evidenceGroup: 'software-rendering',
      requires: ['webgl'],
    },
    evaluate(signals) {
      if (!signals.webgl?.isSoftwareRenderer) return null;
      const renderer =
        signals.webgl.v2?.unmaskedRenderer ?? signals.webgl.v1?.unmaskedRenderer ?? '—';

      return {
        confidence: 0.85,
        explanation:
          'Графика обсчитывается процессором, а не видеокартой. Так выглядят виртуальные ' +
          'машины, серверы без GPU и браузеры, запущенные в headless-режиме. ' +
          'У обычного пользователя за настольным компьютером такого не бывает.',
        evidence: [{ label: 'Рендерер', actual: renderer }],
      };
    },
  }),

  defineRule({
    meta: {
      id: 'hw.webgpu-profile-sparse',
      title: 'WebGPU-профиль похож на неполную заглушку',
      severity: 'medium',
      category: 'hardware',
      flags: ['ANTIDETECT', 'SPOOFING'],
      weight: 16,
      evidenceTier: 'corroborating',
      evidenceGroup: 'webgpu-profile',
      requires: ['webgpu'],
    },
    evaluate(signals) {
      const webgpu = signals.webgpu;
      if (!webgpu?.supported || webgpu.profileVersion === null) return null;

      const problems: string[] = [];
      if ((webgpu.profiles ?? []).length === 0) problems.push('нет ни одного профиля адаптера');
      for (const profile of webgpu.profiles ?? []) {
        const prefix = profile.powerPreference || 'adapter';
        if (!profile.hash) problems.push(`${prefix}: нет hash адаптера`);
        if (profile.featureCount !== null && profile.featureCount < 3) {
          problems.push(`${prefix}: слишком мало features (${profile.featureCount})`);
        }
        if (profile.limitCount !== null && profile.limitCount < 10) {
          problems.push(`${prefix}: слишком мало limits (${profile.limitCount})`);
        }
      }
      if (problems.length === 0) return null;

      return {
        confidence: 0.58,
        explanation:
          'WebGPU-профиль собирает vendor, architecture, features и limits адаптера. ' +
          'Настоящий Chromium отдаёт десятки лимитов и заметный набор возможностей. ' +
          'Пустой или слишком короткий профиль похож на заглушку вместо настоящего GPU-адаптера.',
        evidence: [
          { label: 'Проблемы профиля', detail: problems.join('; ') },
          { label: 'WebGPU profileHash', actual: webgpu.profileHash ?? '—' },
        ],
      };
    },
  }),

  defineRule({
    meta: {
      id: 'hw.webgpu-webgl-mismatch',
      title: 'WebGPU и WebGL сообщают о разных видеокартах',
      severity: 'high',
      category: 'spoofing',
      flags: ['SPOOFING', 'ANTIDETECT'],
      weight: 32,
      evidenceTier: 'corroborating',
      evidenceGroup: 'gpu-consistency',
      requires: ['webgpu', 'webgl'],
    },
    evaluate(signals) {
      const webgpu = signals.webgpu;
      const webgl = signals.webgl;
      if (!webgpu?.supported || !webgpu.vendor || !webgl?.gpuFamily) return null;
      if (webgl.gpuFamily === 'unknown') return null;

      const gpuVendor = webgpu.vendor.toLowerCase();
      const families: Record<string, RegExp> = {
        apple: /apple/,
        nvidia: /nvidia/,
        amd: /amd|ati/,
        intel: /intel/,
        adreno: /qualcomm|adreno/,
        mali: /arm|mali/,
      };

      const pattern = families[webgl.gpuFamily];
      // Сверяем только там, где обе стороны говорят о реальном железе.
      if (!pattern || pattern.test(gpuVendor)) return null;

      return {
        explanation:
          'WebGPU и WebGL называют разных производителей видеокарты. Это два интерфейса ' +
          'к одному и тому же устройству — они обязаны совпадать. Расхождение означает, ' +
          'что подменён только один из них.',
        evidence: [
          { label: 'WebGPU', actual: webgpu.vendor + (webgpu.architecture ? ` / ${webgpu.architecture}` : '') },
          {
            label: 'WebGL',
            actual: webgl.v2?.unmaskedRenderer ?? webgl.v1?.unmaskedRenderer ?? webgl.gpuFamily,
          },
        ],
      };
    },
  }),

  defineRule({
    meta: {
      id: 'hw.fonts-methods-disagree',
      title: 'Два способа определить шрифты дают разные ответы',
      severity: 'high',
      category: 'spoofing',
      flags: ['SPOOFING', 'ANTIDETECT'],
      weight: 25,
      evidenceTier: 'corroborating',
      evidenceGroup: 'font-consistency',
      requires: ['fonts'],
    },
    evaluate(signals) {
      const fonts = signals.fonts;
      if (fonts?.methodsAgree !== false) return null;

      const byDom = new Set(fonts.detected ?? []);
      const byCanvas = new Set(fonts.canvasDetected ?? []);
      const onlyDom = [...byDom].filter((f) => !byCanvas.has(f));
      const onlyCanvas = [...byCanvas].filter((f) => !byDom.has(f));

      return {
        explanation:
          'Список установленных шрифтов, полученный измерением элемента на странице, ' +
          'сильно расходится со списком, полученным измерением текста в canvas. ' +
          'Это два разных пути к одному и тому же набору шрифтов — расхождение ' +
          'означает, что подменён только один из них.',
        evidence: [
          { label: 'Только по измерению в DOM', detail: onlyDom.slice(0, 10).join(', ') || '—' },
          { label: 'Только по измерению в canvas', detail: onlyCanvas.slice(0, 10).join(', ') || '—' },
        ],
      };
    },
  }),
];
